export const queryCachePolicy = {
  app: {
    // Short stale window: within 60s of first load, navigations serve from
    // cache with no network request (instant back-nav); after it, the next
    // mount quietly refetches. Paired with the default refetchOnMount (refetch
    // only when stale) — do NOT set refetchOnMount: 'always', which refetches
    // on every mount and defeats this window.
    staleTime: 60 * 1000,
    gcTime: 60 * 60 * 1000,
  },
  live: {
    staleTime: 5 * 1000,
    refetchInterval: 10 * 1000,
  },
  liveSummary: {
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  },
  reference: {
    staleTime: 30 * 60 * 1000,
  },
  ephemeral: {
    staleTime: 30 * 1000,
  },
} as const

/**
 * Default `queries` options for the app-wide QueryClient.
 *
 * `onServer` is not a nicety. The server builds a throwaway QueryClient per
 * request and never mounts it, yet every query constructed during that render
 * — a disabled `useQuery` included — schedules its garbage-collection timer
 * for the full gcTime the moment it is built. A Node timer keeps the async
 * context it was created in alive until it fires, and that context is the
 * whole render: request store, props, RSC payload. With the one-hour app
 * gcTime and ~100 crawler renders a minute, that pinned ~2.5 MB per page for
 * an hour, filled the 1 GB ProFinder task in four minutes and had ECS kill it
 * on repeat (Cloudflare 502/503 for a third of all requests, 2026-09-12..14).
 *
 * `Infinity` is React Query's "schedule nothing". The browser keeps the real
 * policy. Upstream: TanStack/query#11320 (unfixed as of 5.102).
 */
export function queryClientDefaults(onServer: boolean) {
  return {
    staleTime: queryCachePolicy.app.staleTime,
    gcTime: onServer ? Infinity : queryCachePolicy.app.gcTime,
    refetchOnWindowFocus: false,
  } as const
}
